import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router';
import { AppShell } from './components/layout/app-shell';
import { AppErrorBoundary } from './components/error-boundary';
import { ProtectedRoute } from './components/auth/protected-route';
import { LiderGuard } from './components/auth/lider-guard';
import { ModuleLockGuard } from './components/auth/module-lock-guard';
import { NotificationToastContainer } from './components/ui/notification-toast';
import { LiderLayout } from './components/layout/lider-layout';
import { PlaceholderPage } from './pages/placeholder-page';
import {
  Building2,
  ShieldAlert,
  FolderOpen,
  Settings,
  SearchX,
} from 'lucide-react';

// Lazy-loaded pages
const LoginPage = lazy(() => import('./pages/auth/login').then((m) => ({ default: m.LoginPage })));
const UsersListPage = lazy(() => import('./pages/users/users-list').then((m) => ({ default: m.UsersListPage })));
const UserFormPage = lazy(() => import('./pages/users/user-form').then((m) => ({ default: m.UserFormPage })));
const TicketsPage = lazy(() => import('./pages/tickets/tickets-page').then((m) => ({ default: m.TicketsPage })));
const TicketDetailPage = lazy(() => import('./pages/tickets/ticket-detail').then((m) => ({ default: m.TicketDetailPage })));
const TicketExecutionPage = lazy(() => import('./pages/tickets/ticket-execution').then((m) => ({ default: m.TicketExecutionPage })));
const RejectionViewPage = lazy(() => import('./pages/tickets/rejection-view').then((m) => ({ default: m.RejectionViewPage })));
const HomeDashboardPage = lazy(() => import('./pages/home/home-dashboard').then((m) => ({ default: m.HomeDashboardPage })));
const PlanMatrizDashboardPage = lazy(() => import('./pages/plan-matriz/plan-matriz-dashboard').then((m) => ({ default: m.PlanMatrizDashboardPage })));
const PlanMatrizListPage = lazy(() => import('./pages/plan-matriz/plan-matriz-list').then((m) => ({ default: m.PlanMatrizListPage })));
const PlanMatrizDetailPage = lazy(() => import('./pages/plan-matriz/plan-matriz-detail').then((m) => ({ default: m.PlanMatrizDetailPage })));
const PlanMatrizMonthlyPage = lazy(() => import('./pages/plan-matriz/plan-matriz-monthly').then((m) => ({ default: m.PlanMatrizMonthlyPage })));
const ExcelUploadPage = lazy(() => import('./pages/excel-ingestion/excel-upload').then((m) => ({ default: m.ExcelUploadPage })));
const MatchResolutionPage = lazy(() => import('./pages/excel-ingestion/match-resolution').then((m) => ({ default: m.MatchResolutionPage })));
const NplUploadPage = lazy(() => import('./pages/excel-ingestion/npl-upload').then((m) => ({ default: m.NplUploadPage })));
const LotePreviewPage = lazy(() => import('./pages/lotes/lote-preview').then((m) => ({ default: m.LotePreviewPage })));
const ClosureDashboardPage = lazy(() => import('./pages/closure/closure-dashboard').then((m) => ({ default: m.ClosureDashboardPage })));
const ClosureDetailPage = lazy(() => import('./pages/closure/closure-detail').then((m) => ({ default: m.ClosureDetailPage })));
const PackageGenerationPage = lazy(() => import('./pages/closure/package-generation').then((m) => ({ default: m.PackageGenerationPage })));

// KPI pages
const KpiDashboardPage = lazy(() => import('./pages/kpi/kpi-dashboard').then((m) => ({ default: m.KpiDashboardPage })));
const KpiTrendsPage = lazy(() => import('./pages/kpi/kpi-trends').then((m) => ({ default: m.KpiTrendsPage })));
const KpiSemaforoPage = lazy(() => import('./pages/kpi/kpi-semaforo').then((m) => ({ default: m.KpiSemaforoPage })));
const KpiComparativePage = lazy(() => import('./pages/kpi/kpi-comparative').then((m) => ({ default: m.KpiComparativePage })));

// Pauta pages
const PautasListPage = lazy(() => import('./pages/pautas/pautas-list').then((m) => ({ default: m.PautasListPage })));
const PautaFormPage = lazy(() => import('./pages/pautas/pauta-form').then((m) => ({ default: m.PautaFormPage })));
const ActividadesPreviasListPage = lazy(() => import('./pages/actividades-previas/actividades-previas-list').then((m) => ({ default: m.ActividadesPreviasListPage })));
const ActividadPreviaDetailPage = lazy(() => import('./pages/actividades-previas/actividades-previa-detail').then((m) => ({ default: m.ActividadPreviaDetailPage })));
const TicketsMantenedorPage = lazy(() => import('./pages/tickets/tickets-mantenedor').then((m) => ({ default: m.TicketsMantenedorPage })));

// Notification pages
const NotificationPreferencesPage = lazy(() => import('./pages/notifications/notification-preferences').then((m) => ({ default: m.NotificationPreferencesPage })));

// Programacion pages
const DescargarProgramacionPage = lazy(() => import('./pages/programacion/descargar-programacion').then((m) => ({ default: m.DescargarProgramacionPage })));

// Profile page
const ProfilePage = lazy(() => import('./pages/profile/profile-page').then((m) => ({ default: m.ProfilePage })));

// Interferencias pages
const InterferenciasListPage = lazy(() => import('./pages/interferencias/interferencias-list').then((m) => ({ default: m.InterferenciasListPage })));

// RESSO pages (Codelco compliance module)
const RessoListPage = lazy(() => import('./pages/resso/RessoListPage').then((m) => ({ default: m.RessoListPage })));
const RessoWorkspacePage = lazy(() => import('./pages/resso/RessoWorkspacePage').then((m) => ({ default: m.RessoWorkspacePage })));
const RessoDashboardPage = lazy(() => import('./pages/resso/RessoDashboardPage').then((m) => ({ default: m.RessoDashboardPage })));
const MiRessoPage = lazy(() => import('./pages/lider/MiRessoPage').then((m) => ({ default: m.MiRessoPage })));

// Charlas pages (FR-COMUNICACION)
const CharlasListPage = lazy(() => import('./pages/charlas/CharlasListPage').then((m) => ({ default: m.CharlasListPage })));
const CharlaFormPage = lazy(() => import('./pages/charlas/CharlaFormPage').then((m) => ({ default: m.CharlaFormPage })));
const CharlaDetailPage = lazy(() => import('./pages/charlas/CharlaDetailPage').then((m) => ({ default: m.CharlaDetailPage })));

// Planes y Programas pages (port Schwager → SIME)
const PlanesListPage = lazy(() => import('./pages/planes/planes-list').then((m) => ({ default: m.PlanesListPage })));
const PlanEditorPage = lazy(() => import('./pages/planes/plan-editor').then((m) => ({ default: m.PlanEditorPage })));
const PlanDetailPage = lazy(() => import('./pages/planes/plan-detail').then((m) => ({ default: m.PlanDetailPage })));
const MisActividadesPage = lazy(() => import('./pages/planes/mis-actividades').then((m) => ({ default: m.MisActividadesPage })));

// Report pages
const ReportTicketsPage = lazy(() => import('./pages/reportes/report-tickets').then((m) => ({ default: m.ReportTicketsPage })));
const ReportTooltimePage = lazy(() => import('./pages/reportes/report-tooltime').then((m) => ({ default: m.ReportTooltimePage })));

// Lider pages
const LiderDashboardPage = lazy(() => import('./pages/lider/lider-dashboard').then((m) => ({ default: m.LiderDashboardPage })));
const LiderExecutionPage = lazy(() => import('./pages/lider/lider-execution').then((m) => ({ default: m.LiderExecutionPage })));
const AsignarLideresPage = lazy(() => import('./pages/lider/asignar-lideres').then((m) => ({ default: m.AsignarLideresPage })));

// Firma pública de actividades previas (public - no auth, no sidebar)
const FirmaPublicaPage = lazy(() => import('./pages/actividades-previas/firma-publica').then((m) => ({ default: m.FirmaPublicaPage })));

// MiniApp pages (public - no auth)
const MiniAppVerifyPage = lazy(() => import('./pages/miniapp/verify-link').then((m) => ({ default: m.VerifyLinkPage })));
const MiniAppTicketListPage = lazy(() => import('./pages/miniapp/ticket-list').then((m) => ({ default: m.TicketListPage })));
const MiniAppTicketDetailPage = lazy(() => import('./pages/miniapp/ticket-detail').then((m) => ({ default: m.TicketDetailPage })));
const MiniAppDecisionPage = lazy(() => import('./pages/miniapp/decision-page').then((m) => ({ default: m.DecisionPage })));
const MiniAppSummaryPage = lazy(() => import('./pages/miniapp/summary').then((m) => ({ default: m.SummaryPage })));
const MiniAppRespaldosPage = lazy(() => import('./pages/miniapp/respaldos').then((m) => ({ default: m.RespaldosPage })));
const MiniAppHistoricoPage = lazy(() => import('./pages/miniapp/historico').then((m) => ({ default: m.HistoricoPage })));
const MiniAppHistoricoPeriodoPage = lazy(() => import('./pages/miniapp/historico-periodo').then((m) => ({ default: m.HistoricoPeriodoPage })));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-sime-text-tertiary">Cargando...</div>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
      <div
        className="flex items-center justify-center w-24 h-24 rounded-2xl mb-6"
        style={{
          boxShadow:
            'inset 4px 4px 8px var(--sime-shadow-dark), inset -4px -4px 8px var(--sime-shadow-light)',
        }}
      >
        <SearchX className="h-12 w-12 text-sime-text-tertiary" />
      </div>
      <h1 className="text-2xl md:text-[28px] font-bold text-sime-text-primary mb-3 tracking-tight-md">
        Pagina no encontrada
      </h1>
      <p className="text-sime-text-secondary text-sm md:text-base max-w-md leading-relaxed mb-6">
        La ruta solicitada no existe. Verifique la URL o vuelva al inicio.
      </p>
      <a
        href="/"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-sime-accent hover:opacity-90 transition-opacity"
      >
        Volver al inicio
      </a>
    </div>
  );
}

function AppShellLayout() {
  return (
    <ProtectedRoute>
      <LiderGuard>
        <AppShell>
          <Suspense fallback={<PageLoader />}>
            <ModuleLockGuard>
              <Outlet />
            </ModuleLockGuard>
          </Suspense>
        </AppShell>
        <NotificationToastContainer />
      </LiderGuard>
    </ProtectedRoute>
  );
}

export function App() {
  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Firma externa por email (PUBLIC - no auth, no sidebar) */}
            <Route path="/firma/:uuid" element={<FirmaPublicaPage />} />

            {/* MiniApp routes (PUBLIC - no auth, no sidebar) */}
            <Route path="/miniapp/verify" element={<MiniAppVerifyPage />} />
            <Route path="/miniapp/lotes/:uuid" element={<MiniAppTicketListPage />} />
            <Route path="/miniapp/lotes/:uuid/tickets/:otId" element={<MiniAppTicketDetailPage />} />
            <Route path="/miniapp/lotes/:uuid/tickets/:otId/decision" element={<MiniAppDecisionPage />} />
            <Route path="/miniapp/lotes/:uuid/summary" element={<MiniAppSummaryPage />} />
            <Route path="/miniapp/lotes/:uuid/respaldos" element={<MiniAppRespaldosPage />} />
            <Route path="/miniapp/lotes/:uuid/historico" element={<MiniAppHistoricoPage />} />
            <Route path="/miniapp/lotes/:uuid/historico/:periodo" element={<MiniAppHistoricoPeriodoPage />} />

            {/* Lider routes (authenticated, minimal layout — no sidebar) */}
            <Route path="/l" element={<LiderLayout />}>
              <Route index element={<LiderDashboardPage />} />
              <Route path="mi-resso" element={<MiRessoPage />} />
              <Route path="charlas" element={<CharlasListPage />} />
              <Route path="charlas/nueva" element={<CharlaFormPage />} />
              <Route path="charlas/:id" element={<CharlaDetailPage />} />
              <Route path="charlas/:id/editar" element={<CharlaFormPage />} />
              <Route path=":id" element={<LiderExecutionPage />} />
            </Route>

            {/* Protected routes inside AppShell */}
            <Route element={<AppShellLayout />}>
              <Route path="/" element={<HomeDashboardPage />} />
              <Route path="/kpi" element={<KpiDashboardPage />} />
              <Route path="/kpi/tendencias" element={<KpiTrendsPage />} />
              <Route path="/kpi/semaforo" element={<KpiSemaforoPage />} />
              <Route path="/kpi/comparativo" element={<KpiComparativePage />} />
              <Route path="/notificaciones/preferencias" element={<NotificationPreferencesPage />} />
              <Route path="/perfil" element={<ProfilePage />} />
              <Route path="/usuarios" element={<UsersListPage />} />
              <Route path="/usuarios/nuevo" element={<UserFormPage />} />
              <Route path="/usuarios/:id/editar" element={<UserFormPage />} />
              <Route path="/tickets" element={<TicketsPage />} />
              <Route path="/tickets/mantenedor" element={<TicketsMantenedorPage />} />
              <Route path="/actividades-previas" element={<ActividadesPreviasListPage />} />
              <Route path="/actividades-previas/:id" element={<ActividadPreviaDetailPage />} />
              <Route path="/tickets/:id" element={<TicketDetailPage />} />
              <Route path="/tickets/:id/ejecutar" element={<TicketExecutionPage />} />
              <Route path="/tickets/:id/rechazo" element={<RejectionViewPage />} />
              <Route path="/plan-matriz" element={<PlanMatrizDashboardPage />} />
              <Route path="/plan-matriz/lista" element={<PlanMatrizListPage />} />
              {/* Legacy alias: previously the standalone monthly page lived here.
                  Now redirected to the unified tickets container with the monthly tab pre-selected. */}
              <Route path="/plan-matriz/mensual" element={<Navigate to="/tickets?tab=mensual" replace />} />
              <Route path="/plan-matriz/mensual-legacy" element={<PlanMatrizMonthlyPage />} />
              <Route path="/plan-matriz/:id" element={<PlanMatrizDetailPage />} />
              <Route path="/excel-ingestion" element={<ExcelUploadPage />} />
              <Route path="/excel-ingestion/npl" element={<NplUploadPage />} />
              <Route path="/excel-ingestion/resolve/:uploadLogId" element={<MatchResolutionPage />} />
              <Route path="/lotes/:id/preview" element={<LotePreviewPage />} />
              <Route path="/pautas" element={<PautasListPage />} />
              <Route path="/pautas/nueva" element={<PautaFormPage />} />
              <Route path="/pautas/:id/editar" element={<PautaFormPage />} />
              <Route path="/cierre" element={<ClosureDashboardPage />} />
              <Route path="/cierre/detalle" element={<ClosureDetailPage />} />
              <Route path="/cierre/paquete" element={<PackageGenerationPage />} />

              {/* Placeholder routes — modules in development */}
              <Route path="/programacion/descargar" element={<DescargarProgramacionPage />} />
              <Route path="/interferencias" element={<InterferenciasListPage />} />
              <Route path="/resso" element={<RessoListPage />} />
              <Route path="/resso/:evaluationId" element={<RessoWorkspacePage />} />
              <Route path="/resso/:evaluationId/dashboard" element={<RessoDashboardPage />} />
              <Route path="/charlas" element={<CharlasListPage />} />
              <Route path="/charlas/nueva" element={<CharlaFormPage />} />
              <Route path="/charlas/:id" element={<CharlaDetailPage />} />
              <Route path="/charlas/:id/editar" element={<CharlaFormPage />} />
              <Route path="/planes" element={<PlanesListPage />} />
              <Route path="/planes/nuevo" element={<PlanEditorPage />} />
              <Route path="/planes/:id" element={<PlanDetailPage />} />
              <Route path="/planes/:id/editar" element={<PlanEditorPage />} />
              <Route path="/mis-actividades" element={<MisActividadesPage />} />
              <Route
                path="/organizacion"
                element={
                  <PlaceholderPage
                    title="Organizacion"
                    description="Estructura organizacional del personal. Visualizacion jerarquica de equipos, supervisores y trabajadores asignados a cada ambito."
                    icon={Building2}
                    features={[
                      'Organigrama interactivo por ambito y subcontrato',
                      'Asignacion de supervisores y jefes de turno',
                      'Gestion de cuadrillas y equipos de trabajo',
                      'Historial de cambios organizacionales',
                    ]}
                  />
                }
              />
              <Route path="/asignar-lideres" element={<AsignarLideresPage />} />
              <Route path="/reportes/tickets" element={<ReportTicketsPage />} />
              <Route path="/reportes/tooltime" element={<ReportTooltimePage />} />
              <Route
                path="/seguridad/peligros"
                element={
                  <PlaceholderPage
                    title="Peligros, Riesgos y Controles"
                    description="Modulo para gestionar peligros y riesgos asociados a cada paso de la OT. Permite identificar, evaluar y definir controles para las actividades de mantenimiento."
                    icon={ShieldAlert}
                    features={[
                      'Identificacion de peligros por actividad de la OT',
                      'Evaluacion de riesgos con matriz de probabilidad e impacto',
                      'Definicion de controles y medidas preventivas',
                      'Integracion con el checklist de ejecucion de la OT',
                    ]}
                  />
                }
              />
              <Route
                path="/seguridad/documental"
                element={
                  <PlaceholderPage
                    title="Sistema Documental"
                    description="Gestion de documentos de seguridad y cumplimiento. Repositorio centralizado para procedimientos, protocolos y certificaciones vigentes."
                    icon={FolderOpen}
                    features={[
                      'Repositorio de procedimientos y protocolos de seguridad',
                      'Control de versiones y aprobaciones de documentos',
                      'Alertas de vencimiento de certificaciones',
                      'Busqueda y filtrado por categoria y estado',
                    ]}
                  />
                }
              />
              <Route
                path="/configuracion"
                element={
                  <PlaceholderPage
                    title="Configuracion"
                    description="Configuracion general del sistema SIME. Parametros de operacion, permisos y preferencias de la plataforma."
                    icon={Settings}
                    features={[
                      'Gestion de roles y permisos de usuario',
                      'Parametros de periodos y ambitos',
                      'Configuracion de notificaciones del sistema',
                      'Preferencias de visualizacion y reportes',
                    ]}
                  />
                }
              />

              {/* Catch-all 404 inside AppShell */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Catch-all 404 outside AppShell (unauthenticated) */}
            <Route
              path="*"
              element={
                <div className="flex items-center justify-center min-h-screen bg-sime-bg px-4">
                  <div className="text-center">
                    <SearchX className="h-12 w-12 mx-auto mb-4 text-sime-text-tertiary" />
                    <h1 className="text-xl font-bold text-sime-text-primary mb-2">
                      Pagina no encontrada
                    </h1>
                    <a href="/login" className="text-sm text-sime-accent hover:underline">
                      Ir al inicio de sesion
                    </a>
                  </div>
                </div>
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}
